<template>
  <div>
    <a-page-header title="WebSocket会话回放">
    </a-page-header>

    <a-card style="margin-top: 16px">
      <a-form layout="inline" style="margin-bottom: 16px">
        <a-form-item label="任务ID">
          <a-input
            v-model:value="filters.task_id"
            placeholder="请输入任务ID"
            style="width: 200px"
            @pressEnter="fetchData"
          />
        </a-form-item>
        <a-form-item label="用户ID">
          <a-input-number
            v-model:value="filters.user_id"
            placeholder="请输入用户ID"
            style="width: 150px"
            @pressEnter="fetchData"
          />
        </a-form-item>
        <a-form-item>
          <a-button type="primary" @click="fetchData">查询</a-button>
          <a-button style="margin-left: 8px" @click="handleReset">重置</a-button>
        </a-form-item>
      </a-form>

      <a-table
        :columns="columns"
        :data-source="dataSource"
        :loading="loading"
        :pagination="pagination"
        row-key="session_id"
        @change="handleTableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'status'">
            <a-tag :color="getStatusColor(record.status)">
              {{ record.status }}
            </a-tag>
          </template>
          <template v-else-if="column.key === 'action'">
            <a-button type="link" @click="handleViewDetail(record)">查看详情</a-button>
            <a-button type="link" @click="handleReplay(record)">回放</a-button>
          </template>
        </template>
      </a-table>
    </a-card>

    <!-- 会话详情弹窗 -->
    <a-modal
      v-model:open="detailVisible"
      title="会话详情"
      width="1000px"
      :footer="null"
    >
      <a-descriptions :column="2" bordered>
        <a-descriptions-item label="会话ID">{{ currentSession?.session_id }}</a-descriptions-item>
        <a-descriptions-item label="任务ID">{{ currentSession?.task_id }}</a-descriptions-item>
        <a-descriptions-item label="用户ID">{{ currentSession?.user_id }}</a-descriptions-item>
        <a-descriptions-item label="状态">{{ currentSession?.status }}</a-descriptions-item>
        <a-descriptions-item label="连接时间">{{ currentSession?.connect_time }}</a-descriptions-item>
        <a-descriptions-item label="断开时间">{{ currentSession?.disconnect_time || '-' }}</a-descriptions-item>
        <a-descriptions-item label="总消息数">{{ currentSession?.total_messages }}</a-descriptions-item>
        <a-descriptions-item label="故障现象" :span="2">{{ currentSession?.fault_phenomenon || '-' }}</a-descriptions-item>
      </a-descriptions>

      <a-divider>消息列表</a-divider>
      <a-table
        :columns="messageColumns"
        :data-source="messageList"
        :loading="messageLoading"
        :pagination="false"
        size="small"
        row-key="id"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'message_type'">
            <a-tag :color="record.message_type === 'client_message' ? 'blue' : 'green'">
              {{ record.message_type === 'client_message' ? '客户端' : '服务端' }}
            </a-tag>
          </template>
          <template v-else-if="column.key === 'message_content'">
            <pre style="margin: 0; max-height: 200px; overflow: auto">{{ JSON.stringify(record.message_content, null, 2) }}</pre>
          </template>
        </template>
      </a-table>
    </a-modal>

    <!-- 回放弹窗 -->
    <a-modal
      v-model:open="replayVisible"
      title="会话回放"
      width="1200px"
      :footer="null"
    >
      <div class="replay-container">
        <div class="replay-controls" style="margin-bottom: 16px">
          <a-button @click="startReplay" :disabled="isReplaying">开始回放</a-button>
          <a-button @click="pauseReplay" :disabled="!isReplaying">暂停</a-button>
          <a-button @click="stopReplay">停止</a-button>
          <a-select
            v-model:value="replayCategory"
            style="width: 200px; margin-left: 16px"
            placeholder="筛选消息分类"
            allow-clear
          >
            <a-select-option value="diagnosis_log">诊断日志</a-select-option>
            <a-select-option value="system_notification">系统通知</a-select-option>
            <a-select-option value="connection">连接</a-select-option>
          </a-select>
        </div>
        <div class="replay-log" ref="replayLog">
          <div
            v-for="(log, index) in replayLogs"
            :key="index"
            :class="['replay-log-item', `log-${log.level || 'info'}`]"
          >
            {{ log.text }}
          </div>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import request from '@/api/request';

export default {
  name: 'WebSocketSessionReplay',
  data() {
    return {
      loading: false,
      dataSource: [],
      filters: {
        task_id: '',
        user_id: undefined
      },
      pagination: {
        current: 1,
        pageSize: 20,
        total: 0,
        showTotal: (total) => `共 ${total} 条`
      },
      columns: [
        { title: '会话ID', dataIndex: 'session_id', key: 'session_id' },
        { title: '任务ID', dataIndex: 'task_id', key: 'task_id' },
        { title: '用户ID', dataIndex: 'user_id', key: 'user_id', width: 100 },
        { title: '状态', key: 'status', width: 120 },
        { title: '连接时间', dataIndex: 'connect_time', key: 'connect_time', width: 180 },
        { title: '总消息数', dataIndex: 'total_messages', key: 'total_messages', width: 100 },
        { title: '操作', key: 'action', width: 150 }
      ],
      detailVisible: false,
      currentSession: null,
      messageList: [],
      messageLoading: false,
      messageColumns: [
        { title: '序号', dataIndex: 'sequence', key: 'sequence', width: 80 },
        { title: '类型', key: 'message_type', width: 100 },
        { title: '分类', dataIndex: 'message_category', key: 'message_category', width: 150 },
        { title: '时间', dataIndex: 'timestamp', key: 'timestamp', width: 180 },
        { title: '内容', key: 'message_content' }
      ],
      replayVisible: false,
      replayCategory: undefined,
      replayLogs: [],
      isReplaying: false,
      replayTimer: null
    };
  },
  mounted() {
    this.fetchData();
  },
  beforeUnmount() {
    this.stopReplay();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.current,
          pageSize: this.pagination.pageSize
        };
        if (this.filters.task_id) {
          params.task_id = this.filters.task_id;
        }
        if (this.filters.user_id) {
          params.user_id = this.filters.user_id;
        }
        
        const response = await request.get('/websocket/sessions', { params });
        
        if (response.code === 200) {
          this.dataSource = response.data.items || [];
          this.pagination.total = response.data.total || 0;
        }
      } catch (error) {
        this.$message.error('获取数据失败: ' + error.message);
      } finally {
        this.loading = false;
      }
    },
    
    handleTableChange(pagination) {
      this.pagination.current = pagination.current;
      this.pagination.pageSize = pagination.pageSize;
      this.fetchData();
    },
    
    handleReset() {
      this.filters = { task_id: '', user_id: undefined };
      this.pagination.current = 1;
      this.fetchData();
    },
    
    getStatusColor(status) {
      const colorMap = {
        'connected': 'green',
        'disconnected': 'default',
        'completed': 'blue'
      };
      return colorMap[status] || 'default';
    },
    
    async handleViewDetail(record) {
      this.currentSession = record;
      this.detailVisible = true;
      this.messageLoading = true;
      
      try {
        const response = await request.get(`/websocket/sessions/${record.session_id}/messages`, {
          params: { pageSize: 1000 }
        });
        
        if (response.code === 200) {
          this.messageList = response.data.items || [];
        }
      } catch (error) {
        this.$message.error('获取消息列表失败: ' + error.message);
      } finally {
        this.messageLoading = false;
      }
    },
    
    async handleReplay(record) {
      this.currentSession = record;
      this.replayVisible = true;
      this.replayLogs = [];
      this.isReplaying = false;
      
      // 加载回放数据
      await this.loadReplayData(record.session_id);
    },
    
    async loadReplayData(sessionId) {
      try {
        const params = {};
        if (this.replayCategory) {
          params.category = this.replayCategory;
        }
        
        const response = await request.get(`/websocket/sessions/${sessionId}/replay`, { params });
        
        if (response.code === 200) {
          this.replayMessages = response.data || [];
        }
      } catch (error) {
        this.$message.error('加载回放数据失败: ' + error.message);
      }
    },
    
    startReplay() {
      if (!this.replayMessages || this.replayMessages.length === 0) {
        this.$message.warning('没有可回放的消息');
        return;
      }
      
      this.isReplaying = true;
      this.replayLogs = [];
      
      // 过滤消息
      let messages = this.replayMessages;
      if (this.replayCategory) {
        messages = messages.filter(m => m.message_category === this.replayCategory);
      }
      
      // 只回放服务端的诊断日志消息
      const logMessages = messages.filter(m => 
        m.message_type === 'server_message' && 
        m.message_category === 'diagnosis_log' &&
        m.message_content.type === 'LOG_BATCH'
      );
      
      let messageIndex = 0;
      const replayNext = () => {
        if (messageIndex >= logMessages.length || !this.isReplaying) {
          this.isReplaying = false;
          return;
        }
        
        const message = logMessages[messageIndex];
        const logs = message.message_content.data.logs || [];
        
        logs.forEach((log, logIndex) => {
          setTimeout(() => {
            if (this.isReplaying) {
              this.replayLogs.push({
                text: log.text,
                level: log.level
              });
              this.scrollReplayLog();
            }
          }, log.delay_ms || 0);
        });
        
        messageIndex++;
        if (messageIndex < logMessages.length) {
          this.replayTimer = setTimeout(replayNext, 1000);
        } else {
          this.isReplaying = false;
        }
      };
      
      replayNext();
    },
    
    pauseReplay() {
      this.isReplaying = false;
      if (this.replayTimer) {
        clearTimeout(this.replayTimer);
        this.replayTimer = null;
      }
    },
    
    stopReplay() {
      this.isReplaying = false;
      if (this.replayTimer) {
        clearTimeout(this.replayTimer);
        this.replayTimer = null;
      }
      this.replayLogs = [];
    },
    
    scrollReplayLog() {
      this.$nextTick(() => {
        const container = this.$refs.replayLog;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    }
  }
};
</script>

<style scoped>
.replay-container {
  height: 600px;
  display: flex;
  flex-direction: column;
}

.replay-log {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: #1e1e1e;
  color: #00ff00;
  font-family: 'Courier New', 'Consolas', monospace;
  border: 1px solid #333;
  border-radius: 4px;
}

.replay-log-item {
  margin: 4px 0;
  line-height: 1.6;
}

.log-info {
  color: #00ff00;
}

.log-warning {
  color: #ffff00;
}

.log-error {
  color: #ff0000;
}

.log-success {
  color: #00ffff;
}
</style>
